import React, { useState, useEffect } from "react";
import api from "../chat/api";
// import { toast } from "react-hot-toast";
import Tooltip from "../RFP_Manager/Projects/Project Page/Tooltip";
import { PlusIcon, ActionIcon } from "./ScoringConfigurationSvgs";
import AddQuestionModal from "./AddQuestionModal";
import RemoveQuestionModal from "./RemoveQuestionModal";
import EditQuestionModal from "./EditQuestionModal";
import toast from "react-hot-toast";
import { PiListChecks } from "react-icons/pi";
import { PageHeader, ViewOnlyBanner, useSettings } from "./settings/ui";

const ScreeningQuestions = () => {
  // Only owners can mutate this page; everyone else (editor/reviewer/member) gets
  // a read-only view with the action controls hidden. Start hidden until the role
  // resolves (null while loading) so the buttons never flash active on tab switch.
  const { orgRole } = useSettings();
  const canEdit = orgRole === "Owner";

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);

  // Remove modal states
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [questionToRemove, setQuestionToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  // State to track the currently open popover
  const [openPopoverId, setOpenPopoverId] = useState(null);

  // Close the popover when clicking anywhere outside
  useEffect(() => {
    const handleClickOutside = () => setOpenPopoverId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const getOrgIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const orgId = getOrgIdFromUrl();
      const response = await api.get(
        `/api/v1/org/evaluation-questions/?organization_id=${orgId}`
      );

      if (response.data && typeof response.data === "object") {
        const customList = Array.isArray(response.data.custom)
          ? response.data.custom
          : [];
        const defaultList = Array.isArray(response.data.default)
          ? response.data.default
          : [];

        const combined = [...customList, ...defaultList].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        const mappedQuestions = combined.map((q) => ({
          id: q.id,
          name: q.text, 
          label: q.is_custom ? "Custom" : "Default", 
          enabled: q.is_selected,
          isCustom: q.is_custom,
        }));

        setQuestions(mappedQuestions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching screening questions:", error);
      toast.error("Failed to fetch screening questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const enabledCount = questions.filter((q) => q.enabled).length;

  const toggleQuestion = async (id) => {
    if (!canEdit) return;
    const orgId = getOrgIdFromUrl();

    // 1. Optimistic Update: Instantly flip the UI so it feels responsive
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, enabled: !q.enabled } : q))
    );

    try {
      await api.post(
        `/api/v1/org/evaluation-questions/${id}/toggle-select/?organization_id=${orgId}`
      );
      
      // 2. SUCCESS: Do nothing! The UI is already optimistically updated.
      // (Removed the response.data overwrite block that was turning your toggle off)
      
    } catch (error) {
      console.error("Error toggling question:", error);
      toast.error("Failed to toggle question");

      // 3. ERROR: Revert the toggle back to its previous state if the API fails
      setQuestions((prev) =>
        prev.map((q) => (q.id === id ? { ...q, enabled: !q.enabled } : q))
      );
    }
  };
  // --- ADD FUNCTIONALITY ---
  const handleAddQuestion = async (text) => {
    if (!canEdit) return;
    try {
      setIsAdding(true);
      const orgId = getOrgIdFromUrl();

      const response = await api.post(`/api/v1/org/evaluation-questions/`, {
        organization_id: parseInt(orgId),
        text: text,
      });

      if (response.data) {
        setIsAdding(false);
        setIsAdded(true);

        setTimeout(() => {
          setIsAdded(false);
          setIsAddModalOpen(false);
          fetchQuestions(); 
        }, 2000);
      }
    } catch (error) {
      console.error("Error adding question:", error);
      
      // NEW: Check for the specific error structure from the backend
      if (error.response && error.response.data && error.response.data.text) {
        // The error is an array ["Ensure..."], so we grab the first item
        const errorMessage = Array.isArray(error.response.data.text) 
          ? error.response.data.text[0] 
          : error.response.data.text;
        
        toast.error(errorMessage);
      } else {
        // Fallback for other errors
        toast.error("Failed to add question");
      }
      
      setIsAdding(false);
    }
  };

  // // --- ADD FUNCTIONALITY ---
  // const handleAddQuestion = async (text) => {
  //   try {
  //     setIsAdding(true);
  //     const orgId = getOrgIdFromUrl();

  //     const response = await api.post(`/api/v1/org/evaluation-questions/`, {
  //       organization_id: parseInt(orgId),
  //       text: text,
  //     });

  //     if (response.data) {
  //       setIsAdding(false);
  //       setIsAdded(true);

  //       setTimeout(() => {
  //         setIsAdded(false);
  //         setIsAddModalOpen(false);
  //         fetchQuestions(); 
  //       }, 2000);
  //     }
  //   } catch (error) {
  //     console.error("Error adding question:", error);
  //     toast.error("Failed to add question");
  //     setIsAdding(false);
  //   }
  // };

  // --- EDIT FUNCTIONALITY ---
  const handleEditClick = (question) => {
    if (!canEdit) return;
    setQuestionToEdit(question);
    setIsEditModalOpen(true);
    setIsEdited(false);
    setIsEditing(false);
  };

  const closeEditModal = () => {
    if (isEditing) return;
    setIsEditModalOpen(false);
    setQuestionToEdit(null);
    setIsEditing(false);
    setIsEdited(false);
  };

  const handleConfirmEdit = async (newText) => {
    if (!questionToEdit) return;

    try {
      setIsEditing(true);
      const orgId = getOrgIdFromUrl();

      await api.patch(
        `/api/v1/org/evaluation-questions/${questionToEdit.id}/?organization_id=${orgId}`,
        {
          text: newText,
          is_active: true // Preserving active state logic from your prompt format
        }
      );

      setIsEditing(false);
      setIsEdited(true);

      setTimeout(() => {
        closeEditModal();
        fetchQuestions();
      }, 2000);

    } catch (error) {
      console.error("Error editing question:", error);
      toast.error("Failed to edit question");
      setIsEditing(false);
    }
  };

  // --- REMOVE FUNCTIONALITY ---
  const handleRemoveClick = (question) => {
    if (!canEdit) return;
    setQuestionToRemove(question);
    setIsRemoveModalOpen(true);
    setIsRemoved(false); 
    setIsRemoving(false);
  };

  const closeRemoveModal = () => {
    if (isRemoving) return;
    setIsRemoveModalOpen(false);
    setQuestionToRemove(null);
    setIsRemoving(false);
    setIsRemoved(false);
  };

  const handleConfirmRemove = async () => {
    if (!questionToRemove) return;

    try {
      setIsRemoving(true);
      const orgId = getOrgIdFromUrl();

      await api.delete(
        `/api/v1/org/evaluation-questions/${questionToRemove.id}/?organization_id=${orgId}`
      );

      setIsRemoving(false);
      setIsRemoved(true);
      
      setTimeout(() => {
        closeRemoveModal();
        fetchQuestions();
      }, 2000);

    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question");
      setIsRemoving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="w-full flex-1 min-h-0 flex flex-col">
        <PageHeader
          title="Screening questions"
          description="Configure questions that AI will answer from RFP documents for every project in this workspace."
        />
        {orgRole !== null && !canEdit && (
          <ViewOnlyBanner icon={PiListChecks}>
            Admins choose which screening questions your team answers before bidding.
          </ViewOnlyBanner>
        )}

        {/* Screening Questions Section */}
        <div className={`bg-[#F9FAFB] rounded-[12px] border border-black/10 px-3 py-2 flex flex-col flex-1 min-h-[240px]`}>
          {/* Section Header - Non-scrollable */}
          <div className="flex-shrink-0">
            <div className="flex items-center justify-between mb-2 border-b pb-[8px] border-black/[0.08]">
              <div>
                <h2 className="text-[15px] font-semibold text-[#18101C] mb-1 font-outfit">
                  Go/No-Go Evaluation Questions
                </h2>
                <p className="text-[14px] text-[#71717A] font-outfit">
                  Toggle questions on to include them in the Go/No-Go process.
                  Turn off to exclude.
                </p>
              </div>

              {canEdit && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#A04A97] hover:bg-[#8E4186] transition-colors"
                  >
                    <PlusIcon className="w-5 h-5 text-white" />
                    <span className="text-[14px] font-medium text-white font-outfit">
                      Add Question
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Criteria Status */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[14px] font-outfit">
                <span className="text-[#71717A] font-medium uppercase border-r pr-[6px] border-black/15">
                  QUESTIONS SET
                </span>
                <span className="text-[#18101C] font-semibold">
                  {enabledCount} Enabled
                </span>
              </div>
            </div>
          </div>

          {/* Question Cards - Scrollable */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-4 border-[#F1EEF3] border-t-[#A04A97] rounded-full animate-spin"></div>
                  <p className="text-[14px] text-[#71717A] font-outfit">
                    Fetching...
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {questions.map((question) => (
                  <div
                    key={question.id}
                    className="bg-white rounded-[8px] border border-black/10 p-3 flex items-start gap-[6px] min-h-[50px]"
                  >
                    {/* Left Colored Bar */}
                    <div className="w-[2px] h-4 bg-[#A04A97] rounded-full shrink-0 self-start mt-[3px]"></div>

                    {/* Question Text */}
                    <div className="flex-1 flex items-start gap-2">
                      <h3
                        className={`text-[15px] font-normal leading-relaxed font-outfit transition-colors ${
                          question.enabled ? "text-[#18101C]" : "text-[#A1A1AA]"
                        }`}
                      >
                        {question.name}
                      </h3>
                      <div className="flex gap-1 ">
                        {/* Label badge */}
                        <span
                          className={`shrink-0 self-start mt-[2px] text-[11px] font-medium font-outfit px-2 py-0.5 rounded-full border ${
                            question.isCustom
                              ? "text-[#7C3AED] bg-[rgba(139,85,221,0.1)] border-transparent"
                              : "text-[#52525B] bg-[#F4F4F5] border-transparent"
                          }`}
                        >
                          {question.label}
                        </span>

                        {question.isCustom && canEdit && (
                          <div className="ml-auto relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenPopoverId(openPopoverId === question.id ? null : question.id);
                              }}
                              className="w-6 h-6 mt-[2px] flex shrink-0 items-center justify-center rounded-md text-[#71717A] hover:bg-black/[0.04] transition-colors text-[18px] leading-none"
                            >
                              <ActionIcon />
                            </button>
                            
                            {/* Popover Menu */}
                            {openPopoverId === question.id && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-0 top-full mt-1 w-[160px] bg-white border border-black/10 rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] py-1 z-50"
                              >
                                <button
                                  className="w-full text-left px-3 py-2 text-[14px] text-[#3F3F46] hover:bg-[#F4F0F7] font-outfit flex items-center gap-2 transition-colors rounded-t-[8px]"
                                  onClick={() => {
                                    setOpenPopoverId(null);
                                    handleEditClick(question); // ✅ Wired to Edit modal
                                  }}
                                >
                                  {/* Edit Icon */}
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                  </svg>
                                  Edit question
                                </button>
                                <button
                                  className="w-full text-left px-3 py-2 text-[14px] text-[#E21919] hover:bg-[rgba(226,25,25,0.06)] font-outfit flex items-center gap-2 transition-colors rounded-b-[8px]"
                                  onClick={() => {
                                    setOpenPopoverId(null);
                                    handleRemoveClick(question);
                                  }}
                                >
                                  {/* Trash Icon */}
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.75 3.5H12.25M4.66667 3.5V2.33333C4.66667 2.02391 4.78958 1.72717 5.00838 1.50838C5.22717 1.28958 5.52391 1.16667 5.83333 1.16667H8.16667C8.47609 1.16667 8.77283 1.28958 8.99162 1.50838C9.21042 1.72717 9.33333 2.02391 9.33333 2.33333V3.5M11.0833 3.5V11.6667C11.0833 11.9761 10.9604 12.2728 10.7416 12.4916C10.5228 12.7104 10.2261 12.8333 9.91667 12.8333H4.08333C3.77391 12.8333 3.47717 12.7104 3.25838 12.4916C3.03958 12.2728 2.91667 11.9761 2.91667 11.6667V3.5H11.0833Z" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.83333 6.41667V9.91667M8.16667 6.41667V9.91667" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Remove Question
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    {canEdit && (
                      <div className="flex items-start pt-[3px] pl-2 shrink-0">
                        <Tooltip
                          content={
                            question.enabled
                              ? "Disable question"
                              : "Enable question"
                          }
                          placement="left"
                        >
                          <button
                            onClick={() => toggleQuestion(question.id)}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                              question.enabled ? "bg-[#A04A97]" : "bg-[#D4D4D8]"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                question.enabled
                                  ? "translate-x-5"
                                  : "translate-x-0"
                              }`}
                            ></div>
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Question Modal */}
      <AddQuestionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddQuestion}
        loading={isAdding}
        isAdded={isAdded}
      />
      
      {/* Edit Question Modal */}
      <EditQuestionModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSave={handleConfirmEdit}
        loading={isEditing}
        isSaved={isEdited}
        initialQuestion={questionToEdit}
      />

      {/* Remove Question Modal */}
      <RemoveQuestionModal
        open={isRemoveModalOpen}
        onClose={closeRemoveModal}
        onConfirm={handleConfirmRemove}
        loading={isRemoving}
        deleted={isRemoved}
      />
    </div>
  );
};

export default ScreeningQuestions;
